import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import './MyAsesoriasPage.css';
import { logError } from '../../utils/logger';
import { formatARS } from '../../utils/currency';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'Fecha por definir';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusBadge = (asesoria) => {
  const now = new Date();
  const startTime = asesoria.start_time ? new Date(asesoria.start_time) : null;
  const endTime = asesoria.end_time ? new Date(asesoria.end_time) : null;

  if (!startTime) {
    return { label: 'Pendiente', variant: 'pending', color: '#f59e0b' };
  }

  if (endTime && endTime < now) {
    return { label: 'Finalizada', variant: 'completed', color: '#10b981' };
  }

  if (startTime <= now && endTime && endTime >= now) {
    return { label: 'En curso', variant: 'active', color: '#3b82f6' };
  }

  if (startTime > now) {
    return { label: 'Programada', variant: 'scheduled', color: '#8b5cf6' };
  }

  return { label: 'Pendiente', variant: 'pending', color: '#f59e0b' };
};

const AsesoriaCard = ({ asesoria }) => {
  const profesor = asesoria.profesor || {};
  const profesorNombre = `${profesor.Nombre || ''} ${profesor.apellido || ''}`.trim() || 'Profesor';
  const profesorFoto = resolveImageUrl(profesor.Foto);
  const especialidad = profesor.Especialidad || 'Sin especialidad';
  const status = getStatusBadge(asesoria);

  return (
    <div className="my-asesoria-card">
      <div className="my-asesoria-card-header">
        {profesorFoto ? (
          <img src={profesorFoto} alt={profesorNombre} className="my-asesoria-profesor-img" />
        ) : (
          <div className="my-asesoria-profesor-placeholder">
            <i className="bi-person-circle"></i>
          </div>
        )}
        <div className="my-asesoria-profesor-info">
          <h3 className="my-asesoria-profesor-name">{profesorNombre}</h3>
          <p className="my-asesoria-profesor-especialidad">{especialidad}</p>
        </div>
        <div 
          className="my-asesoria-status-badge"
          style={{ backgroundColor: `${status.color}20`, color: status.color, borderColor: status.color }}
        >
          {status.label}
        </div>
      </div>
      <div className="my-asesoria-card-body">
        <div className="my-asesoria-time-info">
          <div className="my-asesoria-time-item">
            <i className="bi-calendar-event" style={{ color: '#10b981' }}></i>
            <div>
              <strong>Inicio:</strong>
              <span>{formatDateTime(asesoria.start_time)}</span>
            </div>
          </div>
          {asesoria.end_time && (
            <div className="my-asesoria-time-item">
              <i className="bi-calendar-x" style={{ color: '#ef4444' }}></i>
              <div>
                <strong>Fin:</strong>
                <span>{formatDateTime(asesoria.end_time)}</span>
              </div>
            </div>
          )}
        </div>
        {asesoria.text && (
          <div className="my-asesoria-description">
            <p>{asesoria.text}</p>
          </div>
        )}
        {asesoria.Zoom && (
          <div className="my-asesoria-zoom">
            <i className="bi-camera-video"></i>
            <div>
              <strong>Vía Zoom:</strong>
              <a href={asesoria.Zoom} target="_blank" rel="noopener noreferrer">
                {asesoria.Zoom}
              </a>
            </div>
          </div>
        )}
        <div className="my-asesoria-price">
          <span className="my-asesoria-price-label">Precio pagado:</span>
          <span className="my-asesoria-price-value">{formatARS(parseFloat(asesoria.Precio || 0))}</span>
        </div>
      </div>
    </div>
  );
};

const MyAsesoriasPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { notify } = useNotifications();
  const [asesorias, setAsesorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, scheduled, active, completed

  useEffect(() => {
    if (!user || !token) {
      navigate('/login', { state: { from: '/my-asesorias' } });
      return;
    }
    fetchAsesorias();
  }, [user, token, navigate]);

  const fetchAsesorias = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        buildApiUrl(`/api/asesorias?usuario_id=${user.id}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Error al cargar asesorías');
      const data = await response.json();
      setAsesorias(data.asesorias || []);
    } catch (error) {
      logError('[MyAsesoriasPage] Error fetching asesorías:', error);
      notify({ message: 'Error al cargar asesorías', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredAsesorias = asesorias.filter(asesoria => {
    if (filter === 'all') return true;
    const status = getStatusBadge(asesoria);
    return status.variant === filter;
  });

  const stats = {
    total: asesorias.length,
    scheduled: asesorias.filter(a => getStatusBadge(a).variant === 'scheduled').length,
    active: asesorias.filter(a => getStatusBadge(a).variant === 'active').length,
    completed: asesorias.filter(a => getStatusBadge(a).variant === 'completed').length
  };

  return (
    <div className="my-asesorias-page">
      <div className="my-asesorias-hero">
        <div className="my-asesorias-hero-content">
          <h1 className="my-asesorias-title">Mis Asesorías</h1>
          <p className="my-asesorias-subtitle">
            Gestiona y visualiza todas tus asesorías contratadas
          </p>
        </div>
      </div>

      <div className="my-asesorias-stats">
        <div className="my-asesorias-stats-container">
          <div className="my-asesorias-stat-card">
            <div className="my-asesorias-stat-icon" style={{ backgroundColor: '#e0f2fe', color: '#0ea5e9' }}>
              <i className="bi-calendar-check"></i>
            </div>
            <div className="my-asesorias-stat-content">
              <div className="my-asesorias-stat-value">{stats.total}</div>
              <div className="my-asesorias-stat-label">Total</div>
            </div>
          </div>
          <div className="my-asesorias-stat-card">
            <div className="my-asesorias-stat-icon" style={{ backgroundColor: '#f3e8ff', color: '#8b5cf6' }}>
              <i className="bi-calendar-event"></i>
            </div>
            <div className="my-asesorias-stat-content">
              <div className="my-asesorias-stat-value">{stats.scheduled}</div>
              <div className="my-asesorias-stat-label">Programadas</div>
            </div>
          </div>
          <div className="my-asesorias-stat-card">
            <div className="my-asesorias-stat-icon" style={{ backgroundColor: '#dbeafe', color: '#3b82f6' }}>
              <i className="bi-play-circle"></i>
            </div>
            <div className="my-asesorias-stat-content">
              <div className="my-asesorias-stat-value">{stats.active}</div>
              <div className="my-asesorias-stat-label">En curso</div>
            </div>
          </div>
          <div className="my-asesorias-stat-card">
            <div className="my-asesorias-stat-icon" style={{ backgroundColor: '#d1fae5', color: '#10b981' }}>
              <i className="bi-check-circle"></i>
            </div>
            <div className="my-asesorias-stat-content">
              <div className="my-asesorias-stat-value">{stats.completed}</div>
              <div className="my-asesorias-stat-label">Finalizadas</div>
            </div>
          </div>
        </div>
      </div>

      <div className="my-asesorias-filters">
        <div className="my-asesorias-filters-container">
          <button
            className={`my-asesorias-filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todas
          </button>
          <button
            className={`my-asesorias-filter-btn ${filter === 'scheduled' ? 'active' : ''}`}
            onClick={() => setFilter('scheduled')}
          >
            Programadas
          </button>
          <button
            className={`my-asesorias-filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            En curso
          </button>
          <button
            className={`my-asesorias-filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Finalizadas
          </button>
        </div>
      </div>

      <div className="my-asesorias-content">
        <div className="my-asesorias-container">
          {loading ? (
            <div className="my-asesorias-loading">
              <div className="my-asesorias-spinner"></div>
              <p>Cargando asesorías...</p>
            </div>
          ) : filteredAsesorias.length === 0 ? (
            <div className="my-asesorias-empty">
              <i className="bi-calendar-x"></i>
              <h3>
                {asesorias.length === 0 
                  ? 'No tienes asesorías contratadas'
                  : `No hay asesorías ${filter === 'all' ? '' : filter === 'scheduled' ? 'programadas' : filter === 'active' ? 'en curso' : 'finalizadas'}`
                }
              </h3>
              <p>
                {asesorias.length === 0
                  ? 'Cuando contrates una asesoría, aparecerá aquí.'
                  : 'Intenta cambiar el filtro para ver más resultados.'
                }
              </p>
              {asesorias.length === 0 && (
                <button
                  className="my-asesorias-browse-btn"
                  onClick={() => navigate('/shop/asesorias')}
                >
                  <i className="bi-search"></i>
                  Explorar Asesorías
                </button>
              )}
            </div>
          ) : (
            <div className="my-asesorias-grid">
              {filteredAsesorias.map((asesoria) => (
                <AsesoriaCard key={asesoria.id} asesoria={asesoria} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAsesoriasPage;


